"use client";

import React from "react";
import { ArrowUpRight, Clock, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
function LatestInsightsList() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col">
        {[...Array(3)].map((_, i) => <div key={i} className="w-full" data-index={i}>
            <div className="flex flex-col gap-6 py-10 md:flex-row md:items-start md:justify-between">
              <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/50" />
              <div className="flex flex-col gap-2">
                <div className="h-4 w-24 animate-pulse rounded bg-muted/50" />
                <div className="h-4 w-32 animate-pulse rounded bg-muted/50" />
              </div>
            </div>
            {i < 2 && <Separator className="bg-border" />}
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center text-muted-foreground">
        Unable to load insights at this time.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        No insights found.
      </div>;
  }
  return <div className="flex flex-col">
      {posts.map((post, i) => {
      const wordCount = post.content ? post.content.replace(/<[^>]*>/g, "").split(/\s+/).length : 500;
      const readTime = Math.ceil(wordCount / 200);
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "General";
      return <div key={post.id} className="group relative block" data-index={i}>
            <Link to={post.link || "#"} className="block">
              <div className="flex flex-col gap-6 py-12 transition-colors duration-500 group-hover:bg-secondary/30 md:flex-row md:items-start md:justify-between md:px-6">
                {}
                <div className="w-full md:w-[70%]">
                  <h3 className="font-serif text-3xl font-medium leading-tight transition-transform duration-300 group-hover:translate-x-[10px] md:text-5xl lg:text-6xl">
                    {post.title}
                  </h3>
                </div>

                {}
                <div className="flex w-full flex-row gap-6 md:w-[25%] md:flex-col md:items-end md:gap-2 md:text-right">
                  <div className="flex items-center gap-2 font-mono text-sm tracking-wide text-muted-foreground uppercase">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{readTime} min read</span>
                  </div>
                  <div className="flex items-center gap-2 font-mono text-sm tracking-wide text-muted-foreground uppercase">
                    <Tag className="h-3.5 w-3.5" />
                    <span>Topic: {category}</span>
                  </div>
                  <div className="mt-2 hidden opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:block">
                    <span className="flex items-center gap-1 text-sm font-medium text-primary">
                      Read Article <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
            {}
            {i < posts.length - 1 && <Separator className="bg-border" />}
          </div>;
    })}
    </div>;
}
export default function LatestInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-insights" className="bg-background py-24 md:py-32">
      <div className="container mx-auto px-4">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 border-b border-border pb-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-serif text-4xl font-medium text-foreground md:text-6xl">
              Latest Insights
            </h2>
            <p className="mt-4 max-w-lg text-lg text-muted-foreground">
              Evidence-based guides and clarifications on dental health, designed to empower your daily care.
            </p>
          </div>
          <Link to="/KnowledgeHubPage" className="hidden md:block">
            <div className="group flex items-center gap-2 border-b border-primary pb-1 text-sm font-medium uppercase tracking-widest text-primary transition-opacity hover:opacity-70">
              View All Articles
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </div>
          </Link>
        </div>

        <PostsProvider wp_query={wp_query}>
          <LatestInsightsList />
        </PostsProvider>

        <div className="mt-12 md:hidden">
          <Link to="/KnowledgeHubPage">
            <div className="flex w-full items-center justify-center gap-2 rounded-full border border-border bg-background py-4 text-sm font-medium uppercase tracking-widest text-foreground transition-colors hover:bg-secondary">
              View All Articles
              <ArrowUpRight className="h-4 w-4" />
            </div>
          </Link>
        </div>
      </div>
    </section>;
}